import React, { useMemo, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_INVENTORY_RECONCILIATION_REPORT,
  NOTIFY_EMPLOYEE_INVENTORY_MISMATCH,
} from "./graphql";
import { GET_BOOKINGS } from "../AdminBookings/graphql";
import OrderDetailsModal from "../AdminBookings/OrderDetailsModal";

function formatDate(d) {
  return d.toISOString().split("T")[0];
}

function defaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 6); // last 7 days, inclusive
  return { startDate: formatDate(start), endDate: formatDate(end) };
}

function formatOrderDate(value) {
  if (!value) return "N/A";
  const num = Number(value);
  const date = Number.isNaN(num) ? new Date(value) : new Date(num);
  return date.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" });
}

const InventoryReconciliationReport = ({ employees }) => {
  const [{ startDate, endDate }, setDateRange] = useState(defaultDateRange());
  const [employeeId, setEmployeeId] = useState("ALL");
  const [expandedEmployees, setExpandedEmployees] = useState({});
  const [onlyIssues, setOnlyIssues] = useState(false);
  // { [employeeId]: categoryId | null } - filters that employee's mismatch table
  const [categoryFilterByEmployee, setCategoryFilterByEmployee] = useState({});

  const [viewingOrderId, setViewingOrderId] = useState(null);
  const [viewingBooking, setViewingBooking] = useState(null);
  const [fetchBooking] = useMutation(GET_BOOKINGS);

  const [notifyMismatch] = useMutation(NOTIFY_EMPLOYEE_INVENTORY_MISMATCH);
  const [notifyStateByOrder, setNotifyStateByOrder] = useState({});
  // { [orderId]: { loading, status, message } }

  const { data, loading, error } = useQuery(GET_INVENTORY_RECONCILIATION_REPORT, {
    variables: {
      startDate,
      endDate,
      employeeId: employeeId === "ALL" ? null : employeeId,
    },
    fetchPolicy: "network-only",
  });

  const response = data?.getInventoryReconciliationReport;
  const summary = response?.data?.summary;
  const employeeReports = response?.data?.employees || [];

  const visibleEmployees = useMemo(() => {
    if (!onlyIssues) return employeeReports;
    return employeeReports.filter(
      (emp) => emp.driftOrderCount > 0 || emp.mismatchOrderCount > 0
    );
  }, [employeeReports, onlyIssues]);

  const toggleEmployee = (id) => {
    setExpandedEmployees((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const openOrderDetails = async (orderId) => {
    setViewingOrderId(orderId);
    setViewingBooking(null);
    try {
      const res = await fetchBooking({ variables: { data: { id: orderId } } });
      const booking = res?.data?.getBookings?.orders?.[0];
      setViewingBooking(booking || null);
    } catch (e) {
      console.error(e);
    }
  };

  const handleNotify = async (orderId) => {
    setNotifyStateByOrder((prev) => ({
      ...prev,
      [orderId]: { loading: true },
    }));
    try {
      const res = await notifyMismatch({ variables: { orderId } });
      const result = res?.data?.notifyEmployeeInventoryMismatch;
      setNotifyStateByOrder((prev) => ({
        ...prev,
        [orderId]: { loading: false, ...result },
      }));
    } catch (e) {
      setNotifyStateByOrder((prev) => ({
        ...prev,
        [orderId]: { loading: false, status: "ERROR", message: e.message },
      }));
    }
  };

  return (
    <div className="space-y-6">
      {/* FILTERS */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:flex-wrap bg-white border border-gray-100 rounded-2xl shadow-sm p-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            From
          </label>
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) =>
              setDateRange((prev) => ({ ...prev, startDate: e.target.value }))
            }
            className="px-3 py-2 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            To
          </label>
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={formatDate(new Date())}
            onChange={(e) =>
              setDateRange((prev) => ({ ...prev, endDate: e.target.value }))
            }
            className="px-3 py-2 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Employee
          </label>
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            <option value="ALL">All Employees</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.name}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 ml-auto">
          <input
            type="checkbox"
            checked={onlyIssues}
            onChange={(e) => setOnlyIssues(e.target.checked)}
            className="rounded border-gray-300"
          />
          Only show employees with a mismatch or drift
        </label>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-600" />
          <span className="ml-3 text-gray-600 text-sm">Loading...</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-4">
          {error.message}
        </div>
      )}

      {response?.status === "ERROR" && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-4">
          {response.message}
        </div>
      )}

      {!loading && !error && summary && (
        <>
          {/* SUMMARY CARDS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
              <p className="text-xs font-medium text-gray-500">Employees</p>
              <p className="mt-2 text-2xl font-semibold text-gray-900">
                {summary.totalEmployees}
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
              <p className="text-xs font-medium text-gray-500">
                Total Unaccounted Parts (Expected − Deducted)
              </p>
              <p
                className={`mt-2 text-2xl font-semibold ${
                  summary.totalGapParts !== 0 ? "text-amber-600" : "text-gray-900"
                }`}
              >
                {summary.totalGapParts}
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
              <p className="text-xs font-medium text-gray-500">
                Orders With Over/Under Cut
              </p>
              <p
                className={`mt-2 text-2xl font-semibold ${
                  summary.totalMismatchOrders > 0 ? "text-amber-600" : "text-gray-900"
                }`}
              >
                {summary.totalMismatchOrders}
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
              <p className="text-xs font-medium text-gray-500">
                Orders With Stale Inventory Rows
              </p>
              <p
                className={`mt-2 text-2xl font-semibold ${
                  summary.totalDriftOrders > 0 ? "text-red-600" : "text-gray-900"
                }`}
              >
                {summary.totalDriftOrders}
              </p>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            "Expected" is the parts each completed booking's service recipe says should be
            consumed. "Deducted" is what was actually scanned/auto-cut from the employee's
            stock. <b>Under-cut</b> means less was deducted than expected (missed scan /
            leakage); <b>over-cut</b> means more was deducted than the recipe called for.
            Stale rows mean the booking was edited after service start and its inventory
            rows never resynced, so the "expected" baseline itself may be wrong.
          </p>

          {/* EMPLOYEE LIST */}
          <div className="space-y-4">
            {visibleEmployees.length === 0 ? (
              <div className="text-center py-10 text-gray-500 bg-white rounded-2xl border border-gray-100 shadow-sm">
                No employees to show for the selected filters.
              </div>
            ) : (
              visibleEmployees.map((emp) => {
                const isExpanded = !!expandedEmployees[emp.employeeId];
                const hasIssue = emp.driftOrderCount > 0 || emp.mismatchOrderCount > 0;
                const activeCategoryFilter = categoryFilterByEmployee[emp.employeeId] || null;
                const activeCategoryName = activeCategoryFilter
                  ? emp.categories.find((c) => c.categoryId === activeCategoryFilter)?.categoryName
                  : null;

                const filteredMismatchOrders = activeCategoryFilter
                  ? emp.mismatchOrders.filter((mo) =>
                      mo.categories.some((c) => c.categoryId === activeCategoryFilter)
                    )
                  : emp.mismatchOrders;

                return (
                  <div
                    key={emp.employeeId}
                    className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => toggleEmployee(emp.employeeId)}
                      className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-semibold text-gray-900 text-sm">
                          {emp.employeeName || emp.employeeId}
                        </h3>
                        <span className="bg-gray-200 text-gray-700 text-xs font-medium px-2 py-1 rounded-full">
                          {emp.totalOrdersInPeriod} orders
                        </span>
                        <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2 py-1 rounded-full">
                          +{emp.totalPeriodAssignedQty} assigned this period
                        </span>
                        <span className="bg-gray-100 text-gray-700 text-xs font-medium px-2 py-1 rounded-full">
                          {emp.totalCurrentAssignedQty} in hand
                        </span>
                        {emp.totalCurrentPendingQty > 0 && (
                          <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">
                            {emp.totalCurrentPendingQty} pending acceptance
                          </span>
                        )}
                        {emp.mismatchOrderCount > 0 && (
                          <span className="bg-amber-100 text-amber-700 text-xs font-medium px-2 py-1 rounded-full">
                            {emp.mismatchOrderCount} mismatched order
                            {emp.mismatchOrderCount > 1 ? "s" : ""}
                          </span>
                        )}
                        {emp.driftOrderCount > 0 && (
                          <span className="bg-red-100 text-red-700 text-xs font-medium px-2 py-1 rounded-full">
                            {emp.driftOrderCount} stale order
                            {emp.driftOrderCount > 1 ? "s" : ""}
                          </span>
                        )}
                        {emp.forceScanCount > 0 && (
                          <span
                            className="bg-orange-100 text-orange-700 text-xs font-medium px-2 py-1 rounded-full"
                            title="Bookings completed with a forced scan (skipped the normal scan-required check)"
                          >
                            {emp.forceScanCount} force-scanned ({Math.round(emp.forceScanRate * 100)}%)
                          </span>
                        )}
                        {emp.writtenOffCount > 0 && (
                          <span className="bg-slate-200 text-slate-700 text-xs font-medium px-2 py-1 rounded-full">
                            {emp.writtenOffCount} written off (damage/loss)
                          </span>
                        )}
                        {emp.rejectedCount > 0 && (
                          <span className="bg-rose-100 text-rose-700 text-xs font-medium px-2 py-1 rounded-full">
                            {emp.rejectedCount} rejected pending stock
                          </span>
                        )}
                        {!hasIssue && (
                          <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-1 rounded-full">
                            Reconciled
                          </span>
                        )}
                      </div>
                      <span className="text-gray-400 text-xs">
                        {isExpanded ? "▲" : "▼"}
                      </span>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-gray-100">
                        {emp.categories.length === 0 ? (
                          <p className="px-6 py-4 text-sm text-gray-500">
                            No inventory activity for this employee in the selected range.
                          </p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-left text-sm">
                              <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                  <th className="px-4 py-2">Category</th>
                                  <th className="px-4 py-2">Currently Has</th>
                                  <th className="px-4 py-2">Pending Accept</th>
                                  <th className="px-4 py-2">Assigned (period)</th>
                                  <th className="px-4 py-2">Min Stock</th>
                                  <th className="px-4 py-2">Expected (period)</th>
                                  <th className="px-4 py-2">Actually Deducted</th>
                                  <th className="px-4 py-2">Gap</th>
                                  <th className="px-4 py-2">Gap Orders</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {emp.categories.map((cat) => (
                                  <tr
                                    key={cat.categoryId}
                                    className={
                                      cat.periodGapParts !== 0 ? "bg-amber-50" : ""
                                    }
                                  >
                                    <td className="px-4 py-2 font-medium">
                                      {cat.categoryName}
                                    </td>
                                    <td className="px-4 py-2">
                                      {cat.isScanRequired
                                        ? cat.currentUsableQty
                                        : cat.currentUsableParts}
                                    </td>
                                    <td className="px-4 py-2">
                                      {cat.isScanRequired
                                        ? cat.currentPendingQty
                                        : cat.currentPendingParts}
                                    </td>
                                    <td className="px-4 py-2">{cat.periodAssignedQty}</td>
                                    <td className="px-4 py-2">{cat.minStock ?? "—"}</td>
                                    <td className="px-4 py-2">
                                      {cat.periodExpectedParts}
                                    </td>
                                    <td className="px-4 py-2">
                                      {cat.periodActualDeductedParts}
                                    </td>
                                    <td
                                      className={`px-4 py-2 font-medium ${
                                        cat.periodGapParts !== 0
                                          ? "text-amber-700"
                                          : "text-gray-500"
                                      }`}
                                    >
                                      {cat.periodGapParts}
                                    </td>
                                    <td className="px-4 py-2">
                                      {cat.gapOrderCount > 0 ? (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setCategoryFilterByEmployee((prev) => ({
                                              ...prev,
                                              [emp.employeeId]:
                                                prev[emp.employeeId] === cat.categoryId
                                                  ? null
                                                  : cat.categoryId,
                                            }))
                                          }
                                          className={`font-medium hover:underline ${
                                            activeCategoryFilter === cat.categoryId
                                              ? "text-blue-800 underline"
                                              : "text-blue-600"
                                          }`}
                                        >
                                          {cat.gapOrderCount}
                                        </button>
                                      ) : (
                                        0
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {emp.driftOrderIds.length > 0 && (
                          <div className="px-6 py-3 text-xs text-gray-500 bg-red-50 border-t border-red-100">
                            Stale order IDs (recipe no longer matches inventory rows —
                            re-run "Generate Inventories" or verify manually):{" "}
                            {emp.driftOrderIds.map((id) => (
                              <button
                                key={id}
                                type="button"
                                onClick={() => openOrderDetails(id)}
                                className="text-blue-600 hover:underline mr-2"
                              >
                                {id.slice(0, 8)}
                              </button>
                            ))}
                            {emp.driftOrderCount > emp.driftOrderIds.length &&
                              ` (+${emp.driftOrderCount - emp.driftOrderIds.length} more)`}
                          </div>
                        )}

                        {/* MISMATCHED ORDERS */}
                        <div className="border-t border-gray-100 px-6 py-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-gray-800">
                              Mismatched Orders (over/under cut){" "}
                              <span className="text-gray-400 font-normal">
                                ({filteredMismatchOrders.length})
                              </span>
                            </h4>
                            {activeCategoryFilter && (
                              <button
                                type="button"
                                onClick={() =>
                                  setCategoryFilterByEmployee((prev) => ({
                                    ...prev,
                                    [emp.employeeId]: null,
                                  }))
                                }
                                className="text-xs text-blue-600 hover:underline"
                              >
                                Filtering by "{activeCategoryName}" · clear
                              </button>
                            )}
                          </div>

                          {filteredMismatchOrders.length === 0 ? (
                            <p className="text-sm text-gray-500">
                              No over/under cut orders{activeCategoryFilter ? " for this category" : ""}.
                            </p>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-left text-sm">
                                <thead className="bg-gray-50 border-b border-gray-100">
                                  <tr>
                                    <th className="px-4 py-2">Booking</th>
                                    <th className="px-4 py-2">Date</th>
                                    <th className="px-4 py-2">Type</th>
                                    <th className="px-4 py-2">Expected</th>
                                    <th className="px-4 py-2">Actual</th>
                                    <th className="px-4 py-2">Gap</th>
                                    <th className="px-4 py-2">Categories</th>
                                    <th className="px-4 py-2">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {filteredMismatchOrders.map((mo) => {
                                    const notifyState = notifyStateByOrder[mo.orderId];
                                    return (
                                      <tr key={mo.orderId}>
                                        <td className="px-4 py-2">
                                          <button
                                            type="button"
                                            onClick={() => openOrderDetails(mo.orderId)}
                                            className="text-blue-600 hover:underline font-medium"
                                          >
                                            {mo.bookingId || mo.orderId.slice(0, 8)}
                                          </button>
                                        </td>
                                        <td className="px-4 py-2 text-gray-500">
                                          {formatOrderDate(mo.bookingDate)}
                                        </td>
                                        <td className="px-4 py-2">
                                          <span
                                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                              mo.mismatchType === "OVER_CUT"
                                                ? "bg-purple-100 text-purple-700"
                                                : "bg-amber-100 text-amber-700"
                                            }`}
                                          >
                                            {mo.mismatchType === "OVER_CUT" ? "Over-cut" : "Under-cut"}
                                          </span>
                                        </td>
                                        <td className="px-4 py-2">{mo.expectedParts}</td>
                                        <td className="px-4 py-2">{mo.actualParts}</td>
                                        <td className="px-4 py-2 font-medium text-amber-700">
                                          {mo.gapParts}
                                        </td>
                                        <td className="px-4 py-2 text-xs text-gray-500">
                                          {mo.categories.map((c) => c.categoryName).join(", ")}
                                        </td>
                                        <td className="px-4 py-2">
                                          <div className="flex items-center gap-2">
                                            <button
                                              type="button"
                                              onClick={() => openOrderDetails(mo.orderId)}
                                              className="text-xs text-blue-600 hover:underline"
                                            >
                                              View
                                            </button>
                                            <button
                                              type="button"
                                              disabled={notifyState?.loading}
                                              onClick={() => handleNotify(mo.orderId)}
                                              className="text-xs px-2 py-1 rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
                                            >
                                              {notifyState?.loading ? "Sending..." : "Notify Employee"}
                                            </button>
                                            {notifyState && !notifyState.loading && (
                                              <span
                                                className={`text-xs ${
                                                  notifyState.status === "SUCCESS"
                                                    ? "text-green-600"
                                                    : "text-red-600"
                                                }`}
                                              >
                                                {notifyState.status === "SUCCESS" ? "Sent" : notifyState.message}
                                              </span>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {viewingOrderId && (
        viewingBooking ? (
          <OrderDetailsModal
            booking={viewingBooking}
            onClose={() => {
              setViewingOrderId(null);
              setViewingBooking(null);
            }}
          />
        ) : (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-2xl shadow-xl p-6 flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gray-600" />
              <span className="text-sm text-gray-600">Loading order...</span>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default InventoryReconciliationReport;
